/**
 * RefreshRateDialog Component
 * 
 * Dialog for refreshing currency rate from API.
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stack,
  Alert,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Currency } from "../../../types";

export interface RefreshRateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  currencyToRefresh: Currency | null;
  defaultCurrency: string;
  fetchedRateFromAPI: number | null;
  fetchingRateFromAPI: boolean;
  refreshingCurrency: boolean;
  formatRate: (rate: number | string) => string;
}

/**
 * Dialog for refreshing currency rate from API
 */
export default function RefreshRateDialog({
  open,
  onClose,
  onConfirm,
  currencyToRefresh,
  defaultCurrency,
  fetchedRateFromAPI,
  fetchingRateFromAPI,
  refreshingCurrency,
  formatRate,
}: RefreshRateDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Update Rate from API</Typography>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={refreshingCurrency}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            After confirmation, the exchange rate for {currencyToRefresh?.code} will be updated from the API.
            The current manually edited rate will be replaced with the latest rate from the API.
          </Alert>
          {currencyToRefresh && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Current rate: {currencyToRefresh.rate === null ? "Not set" : `1 ${currencyToRefresh.code} = ${formatRate(1 / currencyToRefresh.rate)} ${defaultCurrency}`}
              </Typography>
              {fetchingRateFromAPI ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Fetching new rate from API...
                  </Typography>
                </Box>
              ) : fetchedRateFromAPI ? (
                <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: "medium" }}>
                  New rate: 1 {currencyToRefresh.code} = {formatRate(1 / fetchedRateFromAPI)} {defaultCurrency}
                </Typography>
              ) : (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Failed to fetch rate from API. Please try again.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={onClose} 
          disabled={refreshingCurrency}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={refreshingCurrency || !fetchedRateFromAPI || fetchingRateFromAPI}
          startIcon={refreshingCurrency ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{ flex: 1, py: 1.5 }}
        >
          {refreshingCurrency ? "Updating..." : "Update from API"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
